import { Text, Divider, Tooltip, Stack } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { ColumnLayout } from "@/routes/app/-components/column-layout"
import { DetailsCard } from "@/routes/app/-components/details-card"
import { LabeledValue } from "@components/default-list/labeled-value"
import { BorrowResourceObjectActionIcon } from "./resource-object-action-icons"
import { PersonName } from "@components/PersonName"

interface RentTabProps {
  activeBorrow: any
  pastBorrows: any[]
  resourceObjectId: number
  onReturnClick: () => void
  onLendClick: () => void
}

export function RentTab({
  activeBorrow,
  pastBorrows,
  onReturnClick,
  onLendClick,
}: RentTabProps) {
  const { t } = useTranslation("app_server")

  return (
    <ColumnLayout>
      <DetailsCard
        title={t("Status")}
        buttons={
          <BorrowResourceObjectActionIcon
            onClick={activeBorrow ? onReturnClick : onLendClick}
            isBorrowed={!!activeBorrow}
          />
        }
      >
        {activeBorrow ? (
          <>
            <LabeledValue
              label={t("LentTo")}
              value={
                activeBorrow.personId ? (
                  <PersonName personId={activeBorrow.personId} />
                ) : activeBorrow.location ? (
                  activeBorrow.location.name
                ) : (
                  t("Unknown")
                )
              }
            />
            {activeBorrow.resourceObjectBorrowSet && (
              <LabeledValue
                label={t("PartOfDeliveryNote")}
                value={`${activeBorrow.resourceObjectBorrowSet.setNumber}`}
              />
            )}
            {activeBorrow.startDate && (
              <LabeledValue
                label={t("LendingStart")}
                value={getFormattedDate(activeBorrow.startDate)}
              />
            )}
            {activeBorrow.endDate && (
              <LabeledValue
                label={t("ExpectedReturnDate")}
                value={getFormattedDate(activeBorrow.endDate)}
              />
            )}
            {activeBorrow.expectedEndDate && (
              <LabeledValue
                label={t("ExpectedReturnDate")}
                value={getFormattedDate(activeBorrow.expectedEndDate)}
              />
            )}
            {activeBorrow.note && (
              <LabeledValue label={t("Note")} value={activeBorrow.note} />
            )}
          </>
        ) : (
          <Text color="dimmed">{t("NotLent")}</Text>
        )}
      </DetailsCard>

      {pastBorrows.length > 0 && (
        <DetailsCard title={t("LendingHistory")}>
          {pastBorrows.map((borrow, i) => (
            <Stack
              key={i}
              style={{
                marginBottom: i < pastBorrows.length - 1 ? "1rem" : 0,
              }}
            >
              {borrow.startDate && borrow.endDate && (
                <LabeledValue
                  label={t("LendingPeriod")}
                  value={
                    getFormattedDate(borrow.startDate) +
                    " - " +
                    getFormattedDate(borrow.endDate)
                  }
                />
              )}
              <LabeledValue
                label={t("LentTo")}
                value={
                  borrow.personId ? (
                    <PersonName personId={borrow.personId} />
                  ) : borrow.location ? (
                    borrow.location.name
                  ) : (
                    t("Unknown")
                  )
                }
              />
              {borrow.resourceObjectBorrowSet && (
                <LabeledValue
                  label={t("PartOfDeliveryNote")}
                  value={`${borrow.resourceObjectBorrowSet.setNumber}`}
                />
              )}
              {borrow.note && (
                <LabeledValue label={t("Note")} value={borrow.note} />
              )}
              <Divider />
            </Stack>
          ))}
        </DetailsCard>
      )}
    </ColumnLayout>
  )
}
